import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ProviderSummaryPanel.css';

// Complete presentation notes only. No Markdown, raw thinking, or scroll control.
export default function ProviderSummaryPanel({ summary, cardsStarted, isStreaming, criterionStatuses = {} }) {
    const { t } = useTranslation('review');
    const [elapsed, setElapsed] = useState(0);
    const active = isStreaming && summary?.status === 'active';
    useEffect(() => {
        if (!active) return undefined;
        const started = Date.now();
        const timer = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
        return () => clearInterval(timer);
    }, [active]);

    if (!active) return null;
    let message = summary.text || t('sidebar.activity.waiting');
    const batch = summary.batch_activity;
    if (batch?.phase === 'reading') {
        message = batch.topic
            ? t('sidebar.activity.batchReadingTopic', { topic: batch.topic, defaultValue: `Lecture du contrat autour de « ${batch.topic} ».` })
            : batch.reading_status === 'cache_hit'
                ? t('sidebar.activity.batchReadingCache', { defaultValue: 'Les données de lecture déjà disponibles sont réutilisées pour examiner vos critères.' })
                : t('sidebar.activity.batchReading', { defaultValue: 'Lecture des dispositions du contrat pour préparer l’examen des critères sélectionnés.' });
    } else if (batch?.phase === 'criteria') {
        const inProgress = Object.values(criterionStatuses)
            .filter(item => item.batch_id === batch.batch_id && item.status === 'in_progress')
            .sort((a, b) => (a.criterion_order ?? 0) - (b.criterion_order ?? 0));
        const names = inProgress.map(item => item.criterion_title).filter(name => typeof name === 'string' && name.trim());
        const criteria = names.slice(0, 2).map(name => `« ${name.trim()} »`).join(' · ');
        const remaining = names.length - 2;
        const additional = remaining > 0 ? t('sidebar.activity.batchOtherCriteria', { count: remaining, defaultValue: ` et ${remaining} autre(s) critère(s)` }) : '';
        message = criteria
            ? t('sidebar.activity.batchCriteria', { criteria: criteria + additional, defaultValue: `Examen en cours : ${criteria}${additional}.` })
            : batch.source_event === 'criterion_cache_hit'
                ? t('sidebar.activity.batchCriterionCache', { defaultValue: 'Des résultats déjà disponibles sont réutilisés pour un critère. La revue se poursuit.' })
                : t('sidebar.activity.batchCriteriaProgress', { defaultValue: 'La revue se poursuit à partir des critères traités.' });
    } else if (batch?.phase === 'consolidation') {
        message = t('sidebar.activity.batchConsolidation', { defaultValue: 'Rapprochement des constats issus des différents critères, avant l’affichage des commentaires.' });
    }
    return (
        <section className={`analysis-activity ${cardsStarted ? 'is-compact' : ''}`} aria-label={t('sidebar.activity.label')}>
            <div className="analysis-activity__meta">
                <span className="analysis-activity__signal" aria-hidden="true" />
                <span>{t(cardsStarted ? 'sidebar.activity.continuing' : 'sidebar.activity.label')}</span>
                <span className="analysis-activity__elapsed" aria-hidden="true">{elapsed} s</span>
            </div>
            {!cardsStarted && (
                <p className="analysis-activity__note" aria-live="polite" aria-atomic="true">
                    {message}
                </p>
            )}
        </section>
    );
}
